import React, { useMemo, useState } from "react";
import Select, { components as selectComponents } from "react-select";

/**
 * Searchable drop-in replacement for a native <select>.
 *
 * Every dropdown in the app is searchable, so this keeps the exact API the
 * native element had - <option> children, `name`/`value`/`onChange`,
 * `disabled` and the Tailwind `className` - and only swaps the rendering for a
 * react-select combobox with a type-to-filter box.
 *
 * onChange receives an event shaped like a native select change event:
 *   e.target.name / e.target.value          - the picked option
 *   e.target.tagName === "SELECT"           - keeps upperCaseInput() from
 *                                             uppercasing option codes
 *   e.target.selectedOptions[0].dataset.id  - the option's data-id
 *   e.target.selectedOptions[0].text        - the option's visible label
 *
 * so existing handlers keep working unchanged.
 *
 * Scroll pagination: the menu renders `pageSize` options and appends the next
 * page each time the user scrolls near the bottom, so long lists (users,
 * companies, buyers, items) open instantly. Search always runs against the
 * complete option list - not just the pages rendered so far - and its matches
 * are paged the same way.
 */

/** Options rendered per page of the menu. */
const DEFAULT_PAGE_SIZE = 50;

/** How close (px) to the bottom of the menu the next page starts loading. */
const LOAD_MORE_THRESHOLD = 40;

/**
 * Menu list that asks for the next page when scrolled near its end. It reads
 * paging state from selectProps so the component identity stays stable -
 * swapping it on every page would remount the list and jump back to the top.
 */
const PagedMenuList = (props) => {
  const { onLoadMore, pagingInfo } = props.selectProps;

  const handleScroll = (event) => {
    const list = event.currentTarget;
    if (
      list.scrollHeight - list.scrollTop - list.clientHeight <=
      LOAD_MORE_THRESHOLD
    ) {
      onLoadMore?.();
    }
  };

  return (
    <selectComponents.MenuList
      {...props}
      innerProps={{ ...props.innerProps, onScroll: handleScroll }}
    >
      {props.children}
      {pagingInfo?.hasMore && (
        <div className="px-4 py-2 text-center text-xs normal-case text-gray-500">
          Showing {pagingInfo.shown} of {pagingInfo.total} - scroll for more
        </div>
      )}
    </selectComponents.MenuList>
  );
};

const SELECT_COMPONENTS = { IndicatorSeparator: null, MenuList: PagedMenuList };

/** Flatten children, unwrapping fragments, into a list of elements. */
const flattenChildren = (children, out = []) => {
  React.Children.forEach(children, (child) => {
    if (child === null || child === undefined || typeof child === "boolean")
      return;
    if (Array.isArray(child)) {
      flattenChildren(child, out);
      return;
    }
    if (React.isValidElement(child)) {
      if (child.type === React.Fragment) {
        flattenChildren(child.props?.children, out);
        return;
      }
      out.push(child);
    }
  });
  return out;
};

/** Visible text of an <option>, e.g. {name} / {ntn} -> "ACME / 1234567". */
const nodeToText = (node) => {
  if (node === null || node === undefined || typeof node === "boolean")
    return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join("");
  if (React.isValidElement(node)) return nodeToText(node.props?.children);
  return "";
};

const optionFromElement = (element) => {
  const props = element.props || {};
  const label = nodeToText(props.children).replace(/\s+/g, " ").trim();
  return {
    value: props.value === undefined ? label : props.value,
    label,
    isDisabled: Boolean(props.disabled),
    dataId: props["data-id"],
  };
};

const normalizeOption = (option) =>
  option && typeof option === "object"
    ? {
        value: option.value,
        label: String(option.label ?? option.value ?? ""),
        isDisabled: Boolean(option.isDisabled || option.disabled),
        dataId: option.dataId ?? option["data-id"],
      }
    : { value: option, label: String(option ?? ""), isDisabled: false };

const EMPTY_OPTION = { value: "", label: "", dataId: undefined };

/** Space (px) kept between a widened menu and the edges of the window. */
const VIEWPORT_GUTTER = 16;

/**
 * A menu at least `minWidth` px wide - wider than its field when the field is
 * narrow - kept inside the window: it starts under the field and moves left
 * only as far as it must to fit.
 */
export const widenedMenuBox = (rect, minWidth) => {
  const viewport =
    document.documentElement?.clientWidth || window.innerWidth || 0;
  if (!rect || !viewport) return {};
  const width = Math.min(
    Math.max(rect.width, minWidth),
    viewport - VIEWPORT_GUTTER * 2,
  );
  const left = Math.max(
    VIEWPORT_GUTTER,
    Math.min(rect.left, viewport - VIEWPORT_GUTTER - width),
  );
  return { width, left };
};

export const SearchableSelect = ({
  name,
  value,
  onChange,
  onBlur,
  options: optionsProp,
  children,
  disabled = false,
  required = false,
  className = "",
  placeholder,
  isSearchable = true,
  isClearable,
  maxMenuHeight = 250,
  // Optional: the menu opens at least this many px wide, for lists whose
  // labels are longer than the field (the invoice forms' Buyer).
  menuMinWidth,
  noOptionsMessage = "No matches found",
  menuPortal = true,
  pageSize = DEFAULT_PAGE_SIZE,
  ...rest
}) => {
  // An <option value=""> is the prompt ("Select", "No Users Available"), not a
  // value the user picks - it becomes the placeholder.
  const { options, promptLabel } = useMemo(() => {
    const all = optionsProp
      ? optionsProp.map(normalizeOption)
      : flattenChildren(children)
          .filter((child) => child.type === "option")
          .map(optionFromElement);

    const prompt = all.find((option) => option.value === "");
    return {
      options: all.filter((option) => option.value !== ""),
      promptLabel: prompt?.label,
    };
  }, [children, optionsProp]);

  const selected =
    options.find((option) => String(option.value) === String(value ?? "")) ??
    null;

  const [query, setQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(pageSize);

  // Search the complete list, then page the matches.
  const matches = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(search),
    );
  }, [options, query]);

  const visibleOptions = useMemo(
    () => matches.slice(0, visibleCount),
    [matches, visibleCount],
  );
  const hasMore = visibleOptions.length < matches.length;

  const loadMore = () => {
    if (!hasMore) return;
    setVisibleCount((count) =>
      count >= matches.length ? count : count + pageSize,
    );
  };

  const handleInputChange = (input, { action }) => {
    if (action === "input-change" || input !== query) {
      setQuery(input);
      setVisibleCount(pageSize);
    }
  };

  const hasError = className.includes("border-red-500");
  const isUpperCase = className.includes("uppercase");
  const clearable = isClearable ?? Boolean(promptLabel);

  const handleChange = (option) => {
    if (!onChange) return;
    const picked = option || EMPTY_OPTION;
    const dataset = { id: picked.dataId ?? "" };
    const optionElement = {
      value: picked.value,
      label: picked.label,
      text: picked.label,
      dataset,
    };
    const target = {
      name,
      value: picked.value,
      type: "select-one",
      tagName: "SELECT",
      dataset,
      options: optionElement.value === "" ? [] : [optionElement],
      selectedOptions: [optionElement],
      selectedIndex: option ? 0 : -1,
    };
    onChange({
      target,
      currentTarget: target,
      preventDefault: () => {},
      stopPropagation: () => {},
    });
  };

  return (
    <Select
      unstyled
      inputId={name ? `searchable-select-${name}` : undefined}
      name={name}
      value={selected}
      options={visibleOptions}
      filterOption={null}
      onInputChange={handleInputChange}
      onMenuOpen={() => setVisibleCount(pageSize)}
      onLoadMore={loadMore}
      pagingInfo={{
        hasMore,
        shown: visibleOptions.length,
        total: matches.length,
      }}
      onChange={handleChange}
      onBlur={onBlur}
      isDisabled={disabled}
      isClearable={clearable}
      isSearchable={isSearchable}
      required={required}
      placeholder={placeholder ?? promptLabel ?? "Select"}
      maxMenuHeight={maxMenuHeight}
      menuPlacement="auto"
      menuPortalTarget={menuPortal ? document.body : undefined}
      noOptionsMessage={() => noOptionsMessage}
      components={SELECT_COMPONENTS}
      styles={{
        menuPortal: (base, { rect }) => ({
          ...base,
          zIndex: 9999,
          ...(menuMinWidth ? widenedMenuBox(rect, menuMinWidth) : {}),
        }),
      }}
      classNames={{
        // The caller's own classes keep the control looking exactly like the
        // <select> it replaced (width, padding, radius, error border).
        control: ({ isFocused }) =>
          [
            className,
            "flex items-center gap-2 bg-white cursor-pointer transition-colors",
            isFocused && !hasError ? "border-blue-500" : "",
            disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "",
          ]
            .filter(Boolean)
            .join(" "),
        valueContainer: () => "flex flex-1 items-center overflow-hidden",
        singleValue: () => "truncate text-gray-900",
        placeholder: () => "truncate text-gray-400",
        input: () => "m-0 p-0 text-gray-900",
        indicatorsContainer: () => "flex items-center gap-1",
        dropdownIndicator: () => "text-gray-500 hover:text-gray-700",
        clearIndicator: () => "text-gray-400 hover:text-gray-600 cursor-pointer",
        loadingIndicator: () => "text-gray-400",
        menu: () =>
          [
            "mt-1 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg",
            isUpperCase ? "uppercase" : "",
          ]
            .filter(Boolean)
            .join(" "),
        menuList: () => "py-1",
        option: ({ isSelected, isFocused, isDisabled }) =>
          [
            // A widened menu has room for the whole label, so it wraps
            // rather than being cut off.
            menuMinWidth ? "px-4 py-2 text-sm break-words" : "px-4 py-2 text-sm truncate",
            isDisabled
              ? "text-gray-300 cursor-not-allowed"
              : "cursor-pointer text-gray-900",
            isSelected
              ? "bg-blue-600 text-white"
              : isFocused && !isDisabled
                ? "bg-blue-50"
                : "",
          ]
            .filter(Boolean)
            .join(" "),
        noOptionsMessage: () => "px-4 py-2 text-sm text-gray-500",
      }}
      {...rest}
    />
  );
};

export default SearchableSelect;
